import React, { useEffect, useMemo, useState } from "react";
import {
  ShoppingBag,
  StopCircle,
  LayoutGrid,
  ShoppingCart,
  Car,
  Store,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AdminProductService from "../../../Modules/Services/admin.products.service.ts";
import AdminCategoryService from "../../../Modules/Services/admin.categories.service";
import AdminOrdersService from "../../../Modules/Services/admin.orders.service";

const ACCENT = "#e3a063";

const formatRub = (value) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDayLabel = (value) => {
  const s = String(value || "");
  const parts = s.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
  return s;
};

const OrdersChart = ({ series }) => {
  const data = useMemo(() => {
    const arr = Array.isArray(series) ? series : [];
    return arr.map((p) => ({
      day: p.day,
      dayLabel: formatDayLabel(p.day),
      orders: Number(p.orders || 0),
    }));
  }, [series]);

  return (
    <div className="admin-chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="ordersFillAdmin" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCENT} stopOpacity={0.35} />
              <stop offset="100%" stopColor={ACCENT} stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke="rgba(244, 235, 222, 0.08)"
            strokeDasharray="4 4"
          />
          <XAxis
            dataKey="dayLabel"
            tick={{ fill: "rgba(244, 235, 222, 0.55)", fontSize: 12 }}
            axisLine={{ stroke: "rgba(244, 235, 222, 0.12)" }}
            tickLine={{ stroke: "rgba(244, 235, 222, 0.12)" }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: "rgba(244, 235, 222, 0.55)", fontSize: 12 }}
            axisLine={{ stroke: "rgba(244, 235, 222, 0.12)" }}
            tickLine={{ stroke: "rgba(244, 235, 222, 0.12)" }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              border: "1px solid rgba(244, 235, 222, 0.12)",
              background: "#3a302b",
              color: "#f4ebde",
            }}
            labelFormatter={(_, payload) => {
              const p = payload?.[0]?.payload;
              return p?.day ? `Дата: ${p.day}` : _;
            }}
            formatter={(value) => [value, "Заказы"]}
          />
          <Area
            type="monotone"
            dataKey="orders"
            stroke={ACCENT}
            strokeWidth={3}
            fill="url(#ordersFillAdmin)"
            dot={false}
            activeDot={{ r: 5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    stopListProducts: 0,
    totalCategories: 0,
  });
  const [ordersRange, setOrdersRange] = useState("7d");
  const [ordersStats, setOrdersStats] = useState({
    totalOrders: 0,
    goodsSum: 0,
    deliverySum: 0,
    totalSum: 0,
    deliveryOrders: 0,
    pickupOrders: 0,
    series: [],
  });
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [productsData, categoriesData] = await Promise.all([
          AdminProductService.getAllProducts(),
          AdminCategoryService.getAllCategories(),
        ]);

        const allProducts = productsData.flatMap((category) => category.products);
        const stopListProducts = allProducts.filter((p) => p.is_stop_list).length;

        setStats({
          totalProducts: allProducts.length,
          stopListProducts,
          totalCategories: categoriesData.length,
        });
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchOrdersStats = async () => {
      try {
        setOrdersLoading(true);
        const res = await AdminOrdersService.getOrderStats(ordersRange);
        if (res?.success) {
          setOrdersStats({
            ...res.data.summary,
            series: res.data.series || [],
          });
        }
      } catch (e) {
        console.error("Failed to fetch orders stats:", e);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrdersStats();
  }, [ordersRange]);

  const rangeOptions = [
    { label: "1 день", value: "1d" },
    { label: "7 дней", value: "7d" },
    { label: "30 дней", value: "30d" },
    { label: "Всё время", value: "all" },
  ];

  return (
    <div>
      <h1 className="admin-page-title">Дашборд</h1>

      <div className="admin-stat-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">
            <ShoppingBag size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Всего товаров
          </div>
          <div className="admin-stat-card__value">
            {loading ? "…" : stats.totalProducts}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">
            <StopCircle size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            В стоп-листе
          </div>
          <div className="admin-stat-card__value admin-stat-card__value--accent">
            {loading ? "…" : stats.stopListProducts}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">
            <LayoutGrid size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Категорий
          </div>
          <div className="admin-stat-card__value">
            {loading ? "…" : stats.totalCategories}
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__head">
          <h2 className="admin-card__title">Заказы</h2>
          <div className="admin-segmented" role="tablist">
            {rangeOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                role="tab"
                className={ordersRange === o.value ? "is-active" : ""}
                onClick={() => setOrdersRange(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-stat-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">
              <ShoppingCart size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Всего заказов
            </div>
            <div className="admin-stat-card__value">
              {ordersLoading ? "…" : ordersStats.totalOrders}
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">
              <Car size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Доставка
            </div>
            <div className="admin-stat-card__value">
              {ordersLoading ? "…" : ordersStats.deliveryOrders}
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">
              <Store size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Самовывоз
            </div>
            <div className="admin-stat-card__value">
              {ordersLoading ? "…" : ordersStats.pickupOrders}
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">Сумма товаров</div>
            <div className="admin-stat-card__value admin-stat-card__value--accent">
              {ordersLoading ? "…" : formatRub(ordersStats.goodsSum)}
            </div>
          </div>
        </div>

        <div className="admin-stat-grid" style={{ marginTop: "0.5rem" }}>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">Сумма доставки</div>
            <div className="admin-stat-card__value">
              {ordersLoading ? "…" : formatRub(ordersStats.deliverySum)}
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">Итого</div>
            <div className="admin-stat-card__value admin-stat-card__value--accent">
              {ordersLoading ? "…" : formatRub(ordersStats.totalSum)}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "1.25rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.5rem" }}>
            График заказов по дням
          </div>
          <OrdersChart series={ordersStats.series} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
